import URL from 'src/api/url/api'
import themePresets from 'src/model/theme/presets'

/** Course page layouts (theme `layout`), as the learner app numbers them. */
export const LAYOUTS = [
  { value: 0, label: 'Sidebar navigation', rail: true, cardWidth: '100%' },
  { value: 1, label: 'Side arrows', top: true, cardWidth: '55%' },
  { value: 2, label: 'Next button', top: true, cardWidth: '55%', arrow: true },
  { value: 3, label: 'Bottom bar', top: true, cardWidth: '50%', bar: true },
]

export const FONT_SIZES = ['XS', 'S', 'M', 'L', 'XL']

/** Preview heading and paragraph sizes for each font size. */
export const PREVIEW_SIZES = {
  XS: ['14px', '11px'],
  S: ['16px', '12px'],
  M: ['19px', '13px'],
  L: ['22px', '15px'],
  XL: ['25px', '16px'],
}

/** The learner app aligns pages to the top or the center. */
export const ALIGNMENTS = [
  { value: 'top', label: 'Top' },
  { value: 'center', label: 'Center' },
]

/** Theme `stretch`: 2 is the default width, 3 the full width. */
export const STRETCH_FULL = 3
export const STRETCH_DEFAULT = 2

/** Theme fields a preset sets; edits of these count as "modified". */
const PRESET_FIELDS = [
  'font',
  'fontSize',
  'layout',
  'fontColor',
  'buttonColor',
  'backgroundColor',
  'cardBackgroundColor',
  'bodyBackgroundImage',
  'cardAlignment',
  'stretch',
]

const presetImage = preset =>
  preset.theme.bodyBackgroundImage
    ? `${URL.CDN}assets/theme/${preset.name}/${preset.theme.bodyBackgroundImage}`
    : ''

/** The theme a preset applies (its default color variant). */
export const presetTheme = preset => {
  // eslint-disable-next-line no-unused-vars
  const { sampleUrl, ...colors } = preset.colors?.[preset.defaultColor] || {}
  return {
    ...preset.theme,
    bodyBackgroundImage: presetImage(preset),
    ...colors,
    preset: preset.name,
  }
}

export const PRESETS = themePresets.map(preset => {
  const theme = presetTheme(preset)
  return {
    name: preset.name,
    label: preset.previewText || preset.name,
    theme,
  }
})

export const presetOf = theme =>
  PRESETS.find(preset => preset.name === theme?.preset) || null

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/** How many preset fields the theme changed; 0 when it has no known preset. */
export const modifiedCount = theme => {
  const preset = presetOf(theme)
  if (!preset) return 0
  return PRESET_FIELDS.filter(key => !same(theme[key], preset.theme[key]))
    .length
}

const toHexPart = value =>
  Math.max(0, Math.min(255, Math.round(value || 0)))
    .toString(16)
    .padStart(2, '0')

/** `{ r, g, b, a }` to "#RRGGBB" (null when unset or fully transparent). */
export const toHex = color => {
  if (!color) return null
  if (typeof color === 'string') return color.toUpperCase()
  if (color.a === 0) return null
  return `#${toHexPart(color.r)}${toHexPart(color.g)}${toHexPart(
    color.b,
  )}`.toUpperCase()
}

/** "#RRGGBB" to `{ r, g, b, a: 1 }`. */
export const fromHex = hex => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
  a: 1,
})

export const isHex = value => /^#[0-9a-fA-F]{6}$/.test(value || '')

const DEFAULT_BUTTON = '#015FFB'
const DEFAULT_FONT = 'Inter'

/**
 * The theme as learners see it: a button color or font left at the default
 * (#015FFB, Inter) shows the workspace branding's, as the learner app does.
 * Only for display; the saved theme keeps the default, so the course goes on
 * following the branding until a color or font is picked.
 */
export const withBrandingTheme = (theme, branding) => {
  if (!theme || !branding) return theme
  const buttonHex = toHex(theme.buttonColor)
  const brandColor = isHex(branding.primaryColor)
    ? branding.primaryColor.toUpperCase()
    : null
  const useColor =
    brandColor &&
    brandColor !== DEFAULT_BUTTON &&
    (!buttonHex || buttonHex === DEFAULT_BUTTON)
  const brandFont =
    branding.font && branding.font !== DEFAULT_FONT ? branding.font : null
  const useFont = brandFont && (!theme.font || theme.font === DEFAULT_FONT)
  if (!useColor && !useFont) return theme
  return {
    ...theme,
    ...(useColor && { buttonColor: fromHex(brandColor) }),
    ...(useFont && { font: brandFont }),
  }
}

/** CSS color of a theme color; `fallback` when unset. */
export const cssColor = (color, fallback) => {
  if (!color) return fallback
  if (typeof color === 'string') return color
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a ?? 1})`
}
